// src/pages/Playlist.jsx
import React, { useState, useEffect, useContext } from 'react';
import { PlayerContext } from '../context/PlayerContext';
import { useYouTube } from './useYoutube';
import { ListMusic, Play, Plus, Trash2, ChevronRight, Music } from 'lucide-react';
import './Playlist.css';

const PRESET_PLAYLISTS = [
  { id: 'trending-hindi',  label: 'Trending Hindi',   query: 'trending hindi songs 2024',      emoji: '🔥' },
  { id: 'top-bollywood',  label: 'Top Bollywood',     query: 'best bollywood songs 2024',      emoji: '🎬' },
  { id: 'lofi-chill',     label: 'Lofi & Chill',      query: 'lofi chill music hindi',         emoji: '☁️' },
  { id: 'workout-beats',  label: 'Workout Beats',     query: 'best gym workout songs',         emoji: '💪' },
  { id: 'romantic-hits',  label: 'Romantic Hits',     query: 'best romantic hindi songs',      emoji: '❤️' },
  { id: 'party-anthems',  label: 'Party Anthems',     query: 'best party songs hindi 2024',    emoji: '🎉' },
  { id: 'sad-songs',      label: 'Sad Songs',         query: 'best sad hindi songs heartbreak',emoji: '🌧️' },
  { id: 'indie-vibes',    label: 'Indie Vibes',       query: 'best hindi indie songs 2024',    emoji: '🎸' },
];

const Playlist = () => {
  const { playSong, selectedSong } = useContext(PlayerContext) || {};
  const { initialized, searchSongs } = useYouTube();

  const [myPlaylists, setMyPlaylists]     = useState([]);
  const [activePreset, setActivePreset]   = useState(null);
  const [presetSongs, setPresetSongs]     = useState([]);
  const [loadingPreset, setLoadingPreset] = useState(false);
  const [showCreate, setShowCreate]       = useState(false);
  const [newName, setNewName]             = useState('');
  const [openPlaylist, setOpenPlaylist]   = useState(null);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('aura_playlists') || '[]');
      setMyPlaylists(saved);
    } catch (_) {}
  }, []);

  const savePlaylists = (list) => {
    setMyPlaylists(list);
    localStorage.setItem('aura_playlists', JSON.stringify(list));
  };

  const createPlaylist = () => {
    if (!newName.trim()) return;
    const pl = {
      id: Date.now().toString(),
      name: newName.trim(),
      songs: [],
      createdAt: new Date().toISOString(),
    };
    savePlaylists([pl, ...myPlaylists]);
    setNewName('');
    setShowCreate(false);
  };

  const deletePlaylist = (id, e) => {
    e.stopPropagation();
    savePlaylists(myPlaylists.filter(p => p.id !== id));
    if (openPlaylist?.id === id) setOpenPlaylist(null);
  };

  const handlePreset = async (preset) => {
    if (!initialized) return;
    if (activePreset === preset.id) { setActivePreset(null); setPresetSongs([]); return; }
    setActivePreset(preset.id);
    setLoadingPreset(true);
    try {
      const songs = await searchSongs(preset.query, 12);
      setPresetSongs(songs || []);
    } finally {
      setLoadingPreset(false);
    }
  };

  const playPreset = (song) => {
    if (playSong) playSong(song, presetSongs);
  };

  const playMyPlaylist = (song, pl) => {
    if (playSong) playSong(song, pl.songs);
  };

  const formatViews = (v) => {
    if (!v) return '';
    if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
    return v;
  };

  return (
    <div className="playlist-page">

      {/* My Playlists */}
      <section className="pl__section">
        <div className="pl__section-header">
          <div className="pl__section-title">
            <ListMusic size={18} color="#ff7e3a" />
            <h2>My Playlists</h2>
          </div>
          <button className="pl__create-btn" onClick={() => setShowCreate(v => !v)}>
            <Plus size={15} /> New
          </button>
        </div>

        {showCreate && (
          <div className="pl__create-box">
            <input
              className="pl__create-input"
              placeholder="Playlist name..."
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createPlaylist()}
              autoFocus
            />
            <button className="pl__create-confirm" onClick={createPlaylist}>Create</button>
            <button className="pl__create-cancel" onClick={() => setShowCreate(false)}>Cancel</button>
          </div>
        )}

        {myPlaylists.length === 0 && !showCreate ? (
          <div className="pl__empty">
            <Music size={36} color="#e0e0e0" />
            <p>No playlists yet — create one!</p>
          </div>
        ) : (
          <div className="pl__my-grid">
            {myPlaylists.map(pl => (
              <div key={pl.id} className="pl__my-card" onClick={() => setOpenPlaylist(openPlaylist?.id === pl.id ? null : pl)}>
                <div className="pl__my-card-icon">
                  <ListMusic size={22} color="#ff7e3a" />
                </div>
                <div className="pl__my-card-info">
                  <p className="pl__my-card-name">{pl.name}</p>
                  <p className="pl__my-card-count">{pl.songs.length} songs</p>
                </div>
                <div className="pl__my-card-actions">
                  {pl.songs.length > 0 && (
                    <button className="pl__play-btn" onClick={e => { e.stopPropagation(); playSong?.(pl.songs[0], pl.songs); }}>
                      <Play size={13} fill="white" color="white" />
                    </button>
                  )}
                  <button className="pl__delete-btn" onClick={e => deletePlaylist(pl.id, e)}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Expanded playlist songs */}
        {openPlaylist && (
          <div className="pl__open">
            <p className="pl__open-title">{openPlaylist.name}</p>
            {openPlaylist.songs.length === 0 ? (
              <p className="pl__open-empty">No songs in this playlist yet. Search for songs and add them!</p>
            ) : (
              openPlaylist.songs.map((song, idx) => (
                <div
                  key={song.id}
                  className={`pl__song-row${selectedSong?.id === song.id ? ' pl__song-row--active' : ''}`}
                  onClick={() => playMyPlaylist(song, openPlaylist)}
                >
                  <span className="pl__song-num">{idx + 1}</span>
                  <img src={song.thumbnail} alt={song.title} className="pl__song-thumb" />
                  <div className="pl__song-info">
                    <p className="pl__song-title">{song.title}</p>
                    <p className="pl__song-artist">{song.channelTitle}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </section>

      {/* Curated Playlists */}
      <section className="pl__section">
        <div className="pl__section-header">
          <div className="pl__section-title">
            <Music size={18} color="#ff7e3a" />
            <h2>Curated For You</h2>
          </div>
        </div>
        <div className="pl__preset-grid">
          {PRESET_PLAYLISTS.map(preset => (
            <div
              key={preset.id}
              className={`pl__preset-card${activePreset === preset.id ? ' pl__preset-card--active' : ''}`}
              onClick={() => handlePreset(preset)}
            >
              <span className="pl__preset-emoji">{preset.emoji}</span>
              <p className="pl__preset-label">{preset.label}</p>
              <ChevronRight size={14} className="pl__preset-arrow" />
            </div>
          ))}
        </div>

        {/* Preset songs */}
        {activePreset && (
          <div className="pl__preset-songs">
            {loadingPreset ? (
              <div className="pl__loading-row">
                {[...Array(6)].map((_,i) => <div key={i} className="pl__skeleton" />)}
              </div>
            ) : (
              <div className="pl__preset-song-list">
                {presetSongs.map((song, idx) => (
                  <div
                    key={song.id}
                    className={`pl__song-row${selectedSong?.id === song.id ? ' pl__song-row--active' : ''}`}
                    onClick={() => playPreset(song)}
                  >
                    <span className="pl__song-num">{idx + 1}</span>
                    <img src={song.thumbnail} alt={song.title} className="pl__song-thumb" />
                    <div className="pl__song-info">
                      <p className="pl__song-title">{song.title}</p>
                      <p className="pl__song-artist">{song.channelTitle}</p>
                    </div>
                    {song.viewCount && (
                      <span className="pl__song-views">{formatViews(song.viewCount)}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default Playlist;